import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import type { Mechanizace, MechanizaceInput } from '@/types/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { usePermissions } from '@/lib/permissions';

const QUERY_KEY = ['mechanizace'] as const;
const ENDPOINT = '/mechanizace';

/** Tooltip na zakazanych tlacitkach — at je z UI poznat proc jsou vypnuta. */
const BEZ_OPRAVNENI = 'Nemáte oprávnění spravovat číselníky (vyžaduje roli Vedoucí nebo Administrátor).';

type DialogState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; item: Mechanizace }
  | { mode: 'delete'; item: Mechanizace };

export function MechanizacePage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { canSpravovatCiselniky } = usePermissions();
  const [dialog, setDialog] = useState<DialogState>({ mode: 'closed' });

  const listQuery = useQuery<Mechanizace[]>({
    queryKey: QUERY_KEY,
    queryFn: () => api<Mechanizace[]>(ENDPOINT),
  });

  const createMutation = useMutation({
    mutationFn: (input: MechanizaceInput) =>
      api<Mechanizace>(ENDPOINT, { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({ title: 'Mechanizace vytvořena' });
      setDialog({ mode: 'closed' });
    },
    onError: (err: Error) =>
      toast({ title: 'Vytvoření selhalo', description: err.message, variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: MechanizaceInput }) =>
      api<Mechanizace>(`${ENDPOINT}/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({ title: 'Mechanizace upravena' });
      setDialog({ mode: 'closed' });
    },
    onError: (err: Error) =>
      toast({ title: 'Úprava selhala', description: err.message, variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api<void>(`${ENDPOINT}/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({ title: 'Mechanizace smazána' });
      setDialog({ mode: 'closed' });
    },
    onError: (err: Error) =>
      toast({ title: 'Smazání selhalo', description: err.message, variant: 'destructive' }),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold">Mechanizace</h2>
          <p className="text-sm text-muted-foreground">Číselník vozidel a strojů pro operativu</p>
        </div>
        <Button
          onClick={() => setDialog({ mode: 'create' })}
          disabled={!canSpravovatCiselniky}
          title={canSpravovatCiselniky ? undefined : BEZ_OPRAVNENI}
        >
          <Plus className="mr-2 h-4 w-4" />
          Nová mechanizace
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Seznam mechanizací</CardTitle>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : listQuery.isError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              Načtení selhalo: {(listQuery.error as Error).message}
            </div>
          ) : (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-32">Kód</TableHead>
                  <TableHead>Název</TableHead>
                  <TableHead className="w-40">SPZ</TableHead>
                  <TableHead className="w-28">Aktivní</TableHead>
                  <TableHead className="w-40 text-right">Akce</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(listQuery.data ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                      Zatím žádné záznamy.
                    </TableCell>
                  </TableRow>
                ) : (
                  (listQuery.data ?? []).map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="font-mono text-xs">{m.kod}</TableCell>
                      <TableCell>{m.nazev}</TableCell>
                      <TableCell className="font-mono text-xs">{m.spz ?? '—'}</TableCell>
                      <TableCell>
                        {m.aktivni ? (
                          <Badge variant="success">Ano</Badge>
                        ) : (
                          <Badge variant="secondary">Ne</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!canSpravovatCiselniky}
                          title={canSpravovatCiselniky ? 'Upravit' : BEZ_OPRAVNENI}
                          onClick={() => setDialog({ mode: 'edit', item: m })}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!canSpravovatCiselniky}
                          title={canSpravovatCiselniky ? 'Deaktivovat' : BEZ_OPRAVNENI}
                          onClick={() => setDialog({ mode: 'delete', item: m })}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <MechanizaceFormDialog
        state={dialog}
        onClose={() => setDialog({ mode: 'closed' })}
        onSubmit={(input, id) => {
          if (id) updateMutation.mutate({ id, input });
          else createMutation.mutate(input);
        }}
        submitting={createMutation.isPending || updateMutation.isPending}
      />

      <ConfirmDeleteDialog
        state={dialog}
        onClose={() => setDialog({ mode: 'closed' })}
        onConfirm={(id) => deleteMutation.mutate(id)}
        submitting={deleteMutation.isPending}
      />
    </div>
  );
}

interface FormDialogProps {
  state: DialogState;
  onClose: () => void;
  onSubmit: (input: MechanizaceInput, id?: string) => void;
  submitting: boolean;
}

function MechanizaceFormDialog({ state, onClose, onSubmit, submitting }: FormDialogProps) {
  const open = state.mode === 'create' || state.mode === 'edit';
  const editItem = state.mode === 'edit' ? state.item : null;

  const [kod, setKod] = useState('');
  const [nazev, setNazev] = useState('');
  const [spz, setSpz] = useState('');
  const [aktivni, setAktivni] = useState(true);

  // Pri otevreni / prepnuti polozky synchronizujeme form state.
  useEffect(() => {
    if (!open) return;
    if (editItem) {
      setKod(editItem.kod);
      setNazev(editItem.nazev);
      setSpz(editItem.spz ?? '');
      setAktivni(editItem.aktivni);
    } else {
      setKod('');
      setNazev('');
      setSpz('');
      setAktivni(true);
    }
  }, [open, editItem]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit(
      {
        kod: kod.trim(),
        nazev: nazev.trim(),
        spz: spz.trim() === '' ? null : spz.trim(),
        aktivni,
      },
      editItem?.id,
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editItem ? 'Upravit mechanizaci' : 'Nová mechanizace'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="kod">Kód</Label>
            <Input id="kod" required value={kod} onChange={(e) => setKod(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="nazev">Název</Label>
            <Input id="nazev" required value={nazev} onChange={(e) => setNazev(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="spz">SPZ</Label>
            <Input id="spz" value={spz} onChange={(e) => setSpz(e.target.value)} />
          </div>
          <div className="flex items-center gap-2">
            <input
              id="aktivni"
              type="checkbox"
              checked={aktivni}
              onChange={(e) => setAktivni(e.target.checked)}
              className="h-4 w-4 rounded border-input"
            />
            <Label htmlFor="aktivni">Aktivní</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Zrušit
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Uložit'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface ConfirmProps {
  state: DialogState;
  onClose: () => void;
  onConfirm: (id: string) => void;
  submitting: boolean;
}

function ConfirmDeleteDialog({ state, onClose, onConfirm, submitting }: ConfirmProps) {
  const open = state.mode === 'delete';
  const item = state.mode === 'delete' ? state.item : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Smazat mechanizaci?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Opravdu chcete smazat <strong>{item?.nazev}</strong> ({item?.kod})? Tato akce je nevratná.
        </p>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Zrušit
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={submitting}
            onClick={() => item && onConfirm(item.id)}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Smazat'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
